// sim/organisms.ts — the behaviour loop: tick every organism through its caste.
//
// Each tick it snapshots the active set (deterministic order, despawn-safe) and
// dispatches each KIND.Organism to its caste template via the CASTE_BEHAVIORS
// lookup. Two cross-caste rules live here so they aren't duplicated in four
// behaviours:
//   • nest-feeding — a member on/next to its colony nest refuels to full;
//   • regen        — Regenerative genes knit hp back up to the unit's max;
//   • death        — energy <= 0 (starved) or hp <= 0 (slain) ⇒ despawn.
// Per-unit traits come from the genome profile the unit captured at spawn
// (profileRef), so this loop honours genetics without per-tick config lookups.
// Allocation discipline mirrors AntSystem: a reused scratch snapshot, no temp
// objects/closures in the loop.

import { KIND } from '../types.ts';
import type { System, Tick, Caste, EntityId } from '../types.ts';
import type { EntitySystem } from '../engine/EntitySystem.ts';
import type { ColonySystem } from './colony.ts';
import { chebyshev } from '../engine/Pathing.ts';
import { CASTE_BEHAVIORS, killOrganism } from './castes.ts';
import type { CasteBehavior } from './castes.ts';
import type { AutonomyCtx, TaskQueue } from './taskQueue.ts';
import type { GenomeSystem } from './genome.ts';

export class OrganismSystem implements System {
  readonly name = 'organisms';

  // The ctx is the autonomy-widened OrganismCtx (castes.ts is untouched); the
  // optional `tasks` field gates the single high-level-autonomy hook below.
  private readonly ctx: AutonomyCtx;
  private readonly entities: EntitySystem;
  private readonly colonies: ColonySystem;
  private readonly genomes: GenomeSystem;
  private readonly tasks: TaskQueue | undefined;
  private readonly scratch: Int32Array;

  constructor(ctx: AutonomyCtx) {
    this.ctx = ctx;
    this.entities = ctx.entities;
    this.colonies = ctx.colonies;
    this.genomes = ctx.genomes;
    this.tasks = ctx.tasks;
    this.scratch = new Int32Array(ctx.entities.capacity);
  }

  /**
   * Despawn one organism through the CANONICAL death path (`killOrganism`): grid
   * removal, entity free, census `onDeath`, and tick-stamped loot. Exposed so an
   * out-of-loop system (e.g. the rival war-casualty sink) can cull a body without
   * reimplementing the bookkeeping. The caller MUST run AFTER `update` in the tick
   * (so `ctx.clock.tick` is current) — the loot determinism note in docs/BACKLOG. No
   * killer is attributed (abstract attrition awards no mutation energy).
   */
  kill(id: EntityId): void {
    killOrganism(this.ctx, id);
  }

  update(tick: Tick): void {
    // Publish the live tick so killOrganism can stamp loot drops with it.
    this.ctx.clock.tick = tick;

    const e = this.entities;
    const colonies = this.colonies;

    // Snapshot the active set so despawns (combat / starvation) during the tick
    // are safe and processing order is deterministic.
    const active = e.activeView();
    const n = e.aliveCount;
    const scratch = this.scratch;
    for (let i = 0; i < n; i++) scratch[i] = active[i] as number;

    const tasks = this.tasks;

    for (let i = 0; i < n; i++) {
      const id = scratch[i] as number;
      if (e.alive[id] === 0) continue; // killed earlier this tick
      if ((e.kind[id] as number) !== KIND.Organism) continue;

      const caste = e.caste[id] as Caste;
      // HIGH-LEVEL AUTONOMY HOOK: a tasked organism runs its assigned task this
      // tick INSTEAD of its default caste behaviour, but STILL flows into the
      // shared nest-feeding + death checks below. When no scheduler is wired
      // (tasks === undefined) this is byte-identical to the original loop.
      let handled = false;
      if (tasks !== undefined && tasks.hasTask(id)) {
        handled = tasks.runAssignedTask(this.ctx, id);
      }
      if (!handled) {
        (CASTE_BEHAVIORS[caste] as CasteBehavior).update(this.ctx, id);
      }
      if (e.alive[id] === 0) continue; // (defensive) behaviour despawned it

      const p = this.genomes.profile(e.profileRef[id] as number);

      // Nest-feeding: a member on/next to its nest is fed back to full energy.
      const cid = e.colony[id] as number;
      const ax = e.posX[id] as number;
      const ay = e.posY[id] as number;
      if (chebyshev(ax, ay, colonies.homeXAt(cid), colonies.homeYAt(cid)) <= 1) {
        e.energy[id] = p.startEnergy;
      }

      // Regen (Regenerative genes): knit hp back toward this unit's max.
      if (p.regen > 0) {
        const hp = e.hp[id] as number;
        if (hp < p.startHp) e.hp[id] = Math.min(p.startHp, hp + p.regen);
      }

      // Ecosystem-event drain: heat/pesticide/flood bleed extra energy so weak
      // organisms thin out through the NORMAL starvation death path below (we do
      // NOT directly kill). Neutral 0 (or absent) ⇒ byte-identical.
      if (this.ctx.eventMods !== undefined) {
        e.energy[id] = (e.energy[id] as number) - this.ctx.eventMods.organismDrain;
      }

      // Death: starved or slain (counted in ColonySystem.onDeath).
      if ((e.energy[id] as number) <= 0 || (e.hp[id] as number) <= 0) {
        killOrganism(this.ctx, id);
      }
    }
  }
}
